import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useUnit } from "effector-react"
import dayjs from "utils/dayjs"

import { ArticleSearchScopeToLabelMap, FilterCategory } from "config/constants"
import { Guid, IAppliedFilter } from "@/types"
import { addFilter, addFilterHandler, filtersStore, FilterStorageName, removeFilter, removeFilterHandler, replaceFilter, replaceFilterHandler, resetStorage } from "stores/filters"
import useProfitCenters from "hooks/useProfitCenters"
import { formatNumber, getAssetOwnershipTypeLabel, getAssetStoredStatusLabel, getInvoiceAssignmentStatusLabel, getLabeledStatusLabel, getPurposeLabel, getRoleLabel, getValuationStatusLabel } from "utils/utils"
import { getReservationStatusLabel } from "models/Reservation"
import { useTenantSettings } from "stores/tenantSettings"
import useColors from "hooks/useColors"
import { getShippingOrderDisplayStatusLabel, ShippingOrderType } from "models/ShippingOrder"
import { getOutgoingInvoiceStatusLabel, getOutgoingInvoiceTypeLabel } from "models/OutgoingInvoice"
import { getNotificationChannelLabel, getNotificationTypeLabel } from "models/Notification"
import { getOfferStatusLabel } from "models/Offer"
import { useUnits } from "stores/units"
import { ArticleClass, getArticleClassName } from "models/ArticleClassInfo"
import { ShippingMode } from "models/ShippingOrderOverviewItem"
import { getLabelStatusText } from "models/LabelGroup"
import useLabelCategories from "hooks/useLabelCategories"
import useUserEmails from "hooks/useUserEmails"
import { addImplicitProfitCenterFiltersIfNeeded, filterOutNotAllowedProfitCenterFilters } from "stores/profitCenters"
import useTechnicalData from "hooks/useTechnicalData"
import useMaintenanceTypes from "hooks/useMaintenanceTypes"
import { getMaintenanceStatusLabel } from "components/AssetMaintenanceStatusTag/AssetMaintenanceStatusTag"
import { getAssetStatusLabel } from "models/Asset"

interface IUseFilterParams {
    storage?: FilterStorageName
    initialFilters?: IAppliedFilter[]
    filterAppliedFilters?(filters: IAppliedFilter[]): IAppliedFilter[]
    onlyUserProfitCenters?: boolean
}

const emptyFilters: IAppliedFilter[] = []

export default function useFilter({
    storage,
    initialFilters,
    filterAppliedFilters,
    onlyUserProfitCenters,
}: IUseFilterParams) {
    const { t } = useTranslation()
    const storageFilters: IAppliedFilter[] = useUnit(filtersStore).stores[storage || ""]
    const [filters, setFilters] = useState<IAppliedFilter[]>(() => (initialFilters || emptyFilters))
    const [userIds, setUserIds] = useState<Guid[]>([])

    const allAppliedFilters = useMemo(() => (storage ? storageFilters : filters) || emptyFilters, [filters, storage, storageFilters])

    const { tenantProfitCenters } = useProfitCenters()
    const { labelCategories, fetchLabelCategories, getLabelCategoryName } = useLabelCategories()
    const {
        tenantEmployees,
        fetchTenantEmployees,
        tenantWarehouses,
        fetchTenantWarehouses,
    } = useTenantSettings()

    const {
        QuickCouplingSystemTypes,
        ConnectionTypes,
        LockingTypes,
        ControlSystemTypes,
        AgilityTypes,
    } = useTechnicalData()

    const { units } = useUnits()
    const { emails } = useUserEmails({ userIds })
    const { maintenanceTypesMap } = useMaintenanceTypes()
    const { colors, fetchColors } = useColors()

    const appliedFilters = useMemo(() => {
        const fs = onlyUserProfitCenters
            ? filterOutNotAllowedProfitCenterFilters(allAppliedFilters)
            : allAppliedFilters
        return filterAppliedFilters ? filterAppliedFilters(fs) : [...fs]
    }, [allAppliedFilters, filterAppliedFilters, onlyUserProfitCenters])

    const getFilterLabel = useCallback((filter: IAppliedFilter) => {
        if (filter.label) return filter.label

        switch (filter.category) {
            case FilterCategory.articleType:
                return t(ArticleSearchScopeToLabelMap[filter.value])
            case FilterCategory.rentToBuy:
                // Backend delivers 1/0 (or number) – translate to Yes/No
                if (filter.value === 1 || filter.value === true) return t('common.yes')
                if (filter.value === 0 || filter.value === false) return t('common.no')
                return filter.value
            case FilterCategory.assetPurpose:
            case FilterCategory.reservationPurpose:
            case FilterCategory.offerPurpose:
                return getPurposeLabel(filter.value)
            case FilterCategory.availability:
                // Availability quick filter values map to planning state quick filter labels
                // Values may include an array expansion in FilterField (e.g. ["available","notAvailable"]) – here we receive individual values
                const availabilityMap: Record<string, string> = {
                    available: t('availabilityUi.quickFilters.available'),
                    notAvailable: t('availabilityUi.quickFilters.available'), // grouped under Available quick filter
                    planned: t('availabilityUi.quickFilters.reserved'),
                    requestPlanned: t('availabilityUi.quickFilters.reserved'),
                    inUse: t('availabilityUi.quickFilters.inUse'),
                    salePlanned: t('availabilityUi.quickFilters.saleReserved'),
                    salesCrPlanned: t('availabilityUi.quickFilters.saleReserved'),
                    inactive: t('availabilityUi.quickFilters.inactive'),
                    borrowedExpired: t('availabilityUi.quickFilters.available'), // treat overdue borrowed as Available group
                }
                return availabilityMap[filter.value] || filter.value
            case FilterCategory.valuationStatus:
                return getValuationStatusLabel(filter.value)
            case FilterCategory.assignmentStatus:
                return getInvoiceAssignmentStatusLabel(filter.value)
            case FilterCategory.label:
                return getLabeledStatusLabel(filter.value)
            case FilterCategory.reservationStatus:
                return getReservationStatusLabel(filter.value)
            case FilterCategory.salesAdmin:
            case FilterCategory.salesPerson:
                return tenantEmployees
                    ? (tenantEmployees.entities.find(e => e.number === filter.value)?.fullName || filter.value)
                    : t('common.loading')
            case FilterCategory.homebase:
            case FilterCategory.reservationWarehouse:
                if (filter.value === null) {
                    return t("common.noValue")
                }
                if (tenantWarehouses && filter.value === tenantWarehouses.noWarehouseId) {
                    return t("common.noWarehouse")
                }
                return tenantWarehouses
                    ? (tenantWarehouses.entities.find(w => w.id === filter.value)?.description || filter.value)
                    : t('common.loading')
            case FilterCategory.warehouse:
                if (filter.value === null) {
                    return t("common.noWarehouse")
                }
                return tenantWarehouses
                    ? (tenantWarehouses.entities.find(w => w.id === filter.value)?.description || filter.value)
                    : t('common.loading')
            case FilterCategory.fromToLocation:
                return tenantWarehouses
                    ? (tenantWarehouses.entities.find(w => w.id === filter.value)?.description || filter.value)
                    : t('common.loading')
            case FilterCategory.responsibleWarehouse:
                if (filter.value === null) {
                    return t("common.noValue")
                }
                return tenantWarehouses
                    ? (tenantWarehouses.entities.find(w => w.id === filter.value)?.description || filter.value)
                    : t('common.loading')
            case FilterCategory.operationalState:
                return filter.value ? t("asset.assetOperational") : t("asset.assetOutOfOrder")
            case FilterCategory.connectedAssets:
                return filter.value ? t("common.yes") : t("common.no")
            case FilterCategory.ownershipType:
                return getAssetOwnershipTypeLabel(filter.value)
            case FilterCategory.color:
                if (filter.value === null) {
                    return t("colors.noSpecialPaintDefault")
                }
                return colors
                    ? (colors.find(c => c.id === filter.value)?.name || filter.value)
                    : t('common.loading')
            case FilterCategory.shippingOrderType:
                return filter.value === ShippingOrderType.return ? t("shippingOrderType.return") : t("shippingOrderType.delivery")
            case FilterCategory.shippingOrderState:
                return getShippingOrderDisplayStatusLabel(filter.value)
            case FilterCategory.stateOfStorage:
                return getAssetStoredStatusLabel(filter.value)
            case FilterCategory.outgoingInvoiceStatus:
                return getOutgoingInvoiceStatusLabel(filter.value)
            case FilterCategory.outgoingInvoiceType:
                return getOutgoingInvoiceTypeLabel(filter.value)
            case FilterCategory.notificationChannel:
                return getNotificationChannelLabel(filter.value)
            case FilterCategory.notificationType:
                return getNotificationTypeLabel(filter.value, { forFilter: true })
            case FilterCategory.offerStatus:
                return getOfferStatusLabel(filter.value)
            case FilterCategory.shortage:
            case FilterCategory.cuttingEdge:
            case FilterCategory.teeth:
            case FilterCategory.heavyDuty:
                return filter.value ? t('common.yes') : t('common.no')
            case FilterCategory.startsWithinDays:
                return t('filter.startsWithinXDays', { count: filter.value })
            case FilterCategory.endsWithinDays:
                return t('filter.endsWithinXDays', { count: filter.value })
            case FilterCategory.automaticInvoicing:
                return filter.value ? t('filter.automaticInvoicing.automatic') : t('filter.automaticInvoicing.manual')
            case FilterCategory.profitCenter:
                return tenantProfitCenters
                    ? (tenantProfitCenters.items.find(pc => pc.id === filter.value)?.description || filter.value)
                    : t('common.loading')
            case FilterCategory.disposalDateDiffersCreationPeriod:
                return t("filter.disposalDateDiffersCreationPeriod")
            case FilterCategory.disposalDateDiffersInvoicePeriod:
                return t("filter.disposalDateDiffersInvoicePeriod")
            case FilterCategory.invoiceDateDiffersCreationPeriod:
                return t("filter.invoiceDateDiffersCreationDatePeriod")
            case FilterCategory.missingDepreciationRate:
                return t("filter.missingDepreciationRate")
            case FilterCategory.invoicePendingMoreThanDays:
                return t("filter.invoicePendingMoreThanXDays", { count: filter.value })
            case FilterCategory.condition:
                if (typeof filter.value[0] === "number" && typeof filter.value[1] === "number") {
                    if (filter.value[0] === filter.value[1]) {
                        return `${filter.value[0]}%`
                    }
                    return `${filter.value[0]} - ${filter.value[1]}%`
                }
                if (typeof filter.value[0] === "number") {
                    return `${t('filter.from')} ${filter.value[0]}%`
                }
                if (typeof filter.value[1] === "number") {
                    return `${t('filter.to')} ${filter.value[1]}%`
                }
                return ""
            case FilterCategory.length:
            case FilterCategory.width:
            case FilterCategory.height:
            case FilterCategory.volume:
                const unitLabel = filter.category === FilterCategory.volume
                    ? t('common.m3')
                    : ""

                if (typeof filter.value[0] === "number" && typeof filter.value[1] === "number") {
                    if (filter.value[0] === filter.value[1]) {
                        return `${filter.value[0]} ${unitLabel}`
                    }
                    return `${filter.value[0]} - ${filter.value[1]} ${unitLabel}`
                }
                if (typeof filter.value[0] === "number") {
                    return `${t('filter.from')} ${filter.value[0]} ${unitLabel}`
                }
                if (typeof filter.value[1] === "number") {
                    return `${t('filter.to')} ${filter.value[1]} ${unitLabel}`
                }
                return ""
            case FilterCategory.availabilityDate:
                const availabilityDateFormat = t('config.dateTimeFormat')
                if (filter.value[0] && filter.value[1]) {
                    if (dayjs.utc(filter.value[0]).isSame(filter.value[1])) {
                        return dayjs.utc(filter.value[0]).format(availabilityDateFormat)
                    }
                    return `${dayjs.utc(filter.value[0]).format(availabilityDateFormat)} - ${dayjs.utc(filter.value[1]).format(availabilityDateFormat)}`
                }
                if (filter.value[0]) {
                    return `${t('filter.from')} ${dayjs.utc(filter.value[0]).format(availabilityDateFormat)}`
                }
                if (filter.value[1]) {
                    return `${t('filter.to')} ${dayjs.utc(filter.value[1]).format(availabilityDateFormat)}`
                }
                return ""
            case FilterCategory.lastScanDate:
                const lastScanDateFormat = t('config.dateFormat')
                if (filter.value[0] && filter.value[1]) {
                    if (dayjs(filter.value[0]).isSame(filter.value[1])) {
                        return dayjs(filter.value[0]).format(lastScanDateFormat)
                    }
                    return `${dayjs(filter.value[0]).format(lastScanDateFormat)} - ${dayjs(filter.value[1]).format(lastScanDateFormat)}`
                }
                if (filter.value[0]) {
                    return `${t('filter.from')} ${dayjs(filter.value[0]).format(lastScanDateFormat)}`
                }
                if (filter.value[1]) {
                    return `${t('filter.to')} ${dayjs(filter.value[1]).format(lastScanDateFormat)}`
                }
                return ""
            case FilterCategory.articleClass:
                return getArticleClassName(filter.value as ArticleClass)
            case FilterCategory.initialCondition:
                return filter.value === true ? t('asset.usedAsset') : t('asset.newAsset')
            case FilterCategory.shippingOrderCompleteMode:
                return {
                    [ShippingMode.web]: t('shippingOrderCompleteMode.manualViaWeb'),
                    [ShippingMode.mobile]: t('shippingOrderCompleteMode.scanViaLogisticApp'),
                }[filter.value] || filter.value
            case FilterCategory.labelStatus:
                return getLabelStatusText(filter.value)
            case FilterCategory.labelCategory:
                return filter.value ? getLabelCategoryName(filter.value) : t('labelCategories.noCategory')
            case FilterCategory.user:
                return emails[filter.value as Guid] || t('common.loading')
            case FilterCategory.role:
                return getRoleLabel(filter.value)
            case FilterCategory.quickCoupler:
                return QuickCouplingSystemTypes.get(filter.value) || filter.value
            case FilterCategory.connectionType:
                return ConnectionTypes.get(filter.value) || filter.value
            case FilterCategory.locking:
                return LockingTypes.get(filter.value) || filter.value
            case FilterCategory.controlSystem:
                return ControlSystemTypes.get(filter.value) || filter.value
            case FilterCategory.agility:
                return AgilityTypes.get(filter.value) || filter.value
            case FilterCategory.maxDistance:
                return `${formatNumber(filter.value)} ${t('common.km')}`
            case FilterCategory.maintenanceType:
                return maintenanceTypesMap.get(filter.value)?.name || t('common.loading')
            case FilterCategory.maintenanceStatus:
                return getMaintenanceStatusLabel(filter.value)
            case FilterCategory.assetLifeCycle:
                return getAssetStatusLabel(filter.value)
            case FilterCategory.lastInvoice:
                return t('filter.lastInvoice')
            case FilterCategory.invoiceBeforeShipping:
                return t('filter.invoiceBeforeShipping')
            case FilterCategory.invoiceBlocked:
                return t('filter.invoiceBlock')
            default:
                return filter.value
        }
    }, [t, tenantEmployees, tenantWarehouses, colors, tenantProfitCenters, units, getLabelCategoryName, emails,
        QuickCouplingSystemTypes, ConnectionTypes, LockingTypes, ControlSystemTypes, AgilityTypes, maintenanceTypesMap])

    const resultFilters: IAppliedFilter[] = useMemo(() => {
        if (!onlyUserProfitCenters) return appliedFilters
        return addImplicitProfitCenterFiltersIfNeeded(appliedFilters)
    }, [appliedFilters, onlyUserProfitCenters])

    const resultFiltersLabeled = useMemo(() => {
        return appliedFilters.map(filter => {
            return {
                ...filter,
                label: getFilterLabel(filter)
            }
        })
    }, [appliedFilters, getFilterLabel])

    useEffect(() => {
        if (appliedFilters.some(f => (f.category === FilterCategory.salesAdmin || f.category === FilterCategory.salesPerson))
            && !tenantEmployees) {
            fetchTenantEmployees()
        }
    }, [appliedFilters, fetchTenantEmployees, tenantEmployees])

    useEffect(() => {
        if (appliedFilters.some(f => {
            const isCategoryBasedOnWarehouse = f.category === FilterCategory.homebase
                || f.category === FilterCategory.warehouse
                || f.category === FilterCategory.reservationWarehouse
            return isCategoryBasedOnWarehouse && f.value !== null
        }) && !tenantWarehouses) {
            fetchTenantWarehouses()
        }
    }, [appliedFilters, fetchTenantWarehouses, tenantWarehouses])

    useEffect(() => {
        if (appliedFilters.some(f => f.category === FilterCategory.color && f.value !== null)
            && !colors) {
            fetchColors()
        }
    }, [fetchColors, colors, appliedFilters])

    useEffect(() => {
        if (appliedFilters.some(f => f.category === FilterCategory.labelCategory)
            && !labelCategories) {
            fetchLabelCategories()
        }
    }, [appliedFilters, fetchLabelCategories, labelCategories])

    useEffect(() => {
        const userFilters = appliedFilters.filter(f => f.category === FilterCategory.user)
        setUserIds(userFilters.map(f => f.value as Guid))
    }, [appliedFilters])

    const hasFilterBesidesCategoryAll = useMemo(() => {
        return appliedFilters.some(filter => filter.category !== FilterCategory.all)
    }, [appliedFilters])

    const handleFilterAdd = (filterToAdd: IAppliedFilter | IAppliedFilter[]) => {
        if (storage) {
            addFilter({
                store: storage,
                filter: filterToAdd,
            })
            return
        }

        const newFilters: IAppliedFilter[] = []

        if (Array.isArray(filterToAdd)) {
            filterToAdd.forEach(filter => {
                newFilters.push(...addFilterHandler(appliedFilters, filter))
            })
        } else {
            newFilters.push(...addFilterHandler(appliedFilters, filterToAdd))
        }

        setFilters(newFilters)
    }

    const handleFilterReplace = useCallback((filterCategory: FilterCategory, newFilters: IAppliedFilter[]) => {
        if (storage) {
            replaceFilter({
                store: storage,
                filterCategory,
                filters: newFilters,
            })
            return
        }
        setFilters(
            replaceFilterHandler(appliedFilters, filterCategory, newFilters)
        )
    }, [appliedFilters, storage])

    const handleFilterRemove = useCallback((filterToRemove: IAppliedFilter) => {
        if (storage) {
            removeFilter({
                store: storage,
                filter: filterToRemove
            })
            return
        }
        setFilters(
            removeFilterHandler(appliedFilters, filterToRemove)
        )
    }, [appliedFilters, storage])

    const handleAllFiltersRemove = useCallback(() => {
        if (storage) {
            resetStorage(storage)
            return
        }
        setFilters([])
    }, [storage])

    return {
        filters: resultFilters,
        filtersWithoutImplicitProfitCenters: appliedFilters,
        filtersLabeled: resultFiltersLabeled,
        hasFilterBesidesCategoryAll,
        addFilter: handleFilterAdd,
        replaceFilter: handleFilterReplace,
        removeFilter: handleFilterRemove,
        removeAllFilters: handleAllFiltersRemove,
    }
}
